import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Papa from 'papaparse';
import {
    api,
    type BulkExpenseImportRow,
    type BulkImportPreview,
    type BulkImportRowPreview,
    type BulkImportResult,
} from '@/api/client';
import { Analytics } from '@/analytics/events';

// ─── Step types ─────────────────────────────────────────────────────────────
type Step = 'download' | 'upload' | 'preview' | 'success';

// ─── CSV column name → DTO field mapping ────────────────────────────────────
const CSV_COLUMN_MAP: Record<string, keyof BulkExpenseImportRow> = {
    'service date': 'serviceDate',
    'provider': 'provider',
    'description': 'description',
    'amount': 'amount',
    'category': 'category',
    'family member': 'familyMember',
    'hsa account': 'hsaAccount',
    'notes': 'notes',
};

function mapCsvRow(raw: Record<string, string>): BulkExpenseImportRow {
    const result: Partial<BulkExpenseImportRow> = {};
    for (const [key, value] of Object.entries(raw)) {
        const normalized = key.trim().toLowerCase();
        const field = CSV_COLUMN_MAP[normalized];
        if (field) {
            (result as Record<string, string>)[field] = (value ?? '').trim();
        }
    }
    return result as BulkExpenseImportRow;
}

// ─── Progress indicator ──────────────────────────────────────────────────────
function StepIndicator({ current }: { current: Step }) {
    const steps: { key: Step; label: string }[] = [
        { key: 'download', label: 'Get Template' },
        { key: 'upload', label: 'Upload CSV' },
        { key: 'preview', label: 'Preview & Confirm' },
        { key: 'success', label: 'Done' },
    ];
    const idx = steps.findIndex(s => s.key === current);

    return (
        <div className="flex items-center gap-0 mb-8">
            {steps.map((step, i) => (
                <div key={step.key} className="flex items-center">
                    <div className="flex flex-col items-center">
                        <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${i < idx
                                ? 'bg-[#1B365D] text-white'
                                : i === idx
                                    ? 'bg-[#1B365D] text-white ring-4 ring-[#1B365D]/20'
                                    : 'bg-[#E2E8F0] text-[#718096]'
                                }`}
                        >
                            {i < idx ? (
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                </svg>
                            ) : (
                                i + 1
                            )}
                        </div>
                        <span
                            className={`mt-1 text-[11px] font-medium whitespace-nowrap ${i === idx ? 'text-[#1B365D]' : 'text-[#718096]'
                                }`}
                        >
                            {step.label}
                        </span>
                    </div>
                    {i < steps.length - 1 && (
                        <div
                            className={`flex-1 h-[2px] mx-2 mb-4 transition-colors ${i < idx ? 'bg-[#1B365D]' : 'bg-[#E2E8F0]'
                                }`}
                            style={{ width: '64px', minWidth: '32px' }}
                        />
                    )}
                </div>
            ))}
        </div>
    );
}

// ─── Row preview table ────────────────────────────────────────────────────────
function PreviewTable({
    rows,
    showErrorsOnly,
}: {
    rows: BulkImportRowPreview[];
    showErrorsOnly: boolean;
}) {
    const visible = showErrorsOnly ? rows.filter(r => !r.isValid) : rows;
    if (visible.length === 0) {
        return (
            <p className="text-[13px] text-[#718096] py-4 text-center">
                {showErrorsOnly ? 'No validation errors — all rows are valid! ✓' : 'No rows'}
            </p>
        );
    }

    return (
        <div className="overflow-x-auto rounded-lg border border-[#E2E8F0]">
            <table className="w-full text-[12px]">
                <thead className="bg-[#F7FAFC] sticky top-0 z-10">
                    <tr>
                        {['#', 'Date', 'Provider', 'Description', 'Amount', 'Category', 'Family', 'HSA', 'Status'].map(h => (
                            <th key={h} className="text-left px-3 py-2 font-semibold text-[#4A5568] whitespace-nowrap">
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {visible.map(row => (
                        <tr
                            key={row.rowNumber}
                            className={`border-t border-[#E2E8F0] ${row.isValid ? '' : 'bg-red-50'}`}
                        >
                            <td className="px-3 py-2 text-[#718096]">{row.rowNumber}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{row.serviceDate}</td>
                            <td className="px-3 py-2 max-w-[120px] truncate" title={row.provider}>{row.provider}</td>
                            <td className="px-3 py-2 max-w-[150px] truncate" title={row.description}>{row.description}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{row.amount ? `$${row.amount}` : ''}</td>
                            <td className="px-3 py-2 text-[#718096]">{row.category || '—'}</td>
                            <td className="px-3 py-2 text-[#718096]">{row.familyMember || '—'}</td>
                            <td className="px-3 py-2 text-[#718096]">{row.hsaAccount || '—'}</td>
                            <td className="px-3 py-2">
                                {row.isValid ? (
                                    <span className="text-emerald-600 font-medium">✓ Valid</span>
                                ) : (
                                    <span className="text-red-600 font-medium" title={row.errors.join('\n')}>
                                        ✕ {row.errors[0]}
                                    </span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function ImportPage() {
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [step, setStep] = useState<Step>('download');
    const [parsedRows, setParsedRows] = useState<BulkExpenseImportRow[]>([]);
    const [preview, setPreview] = useState<BulkImportPreview | null>(null);
    const [result, setResult] = useState<BulkImportResult | null>(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [loadingImport, setLoadingImport] = useState(false);
    const [showErrorsOnly, setShowErrorsOnly] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [fileName, setFileName] = useState<string | null>(null);
    const [parseError, setParseError] = useState<string | null>(null);

    // ── Step 1 → Step 2: proceed to upload ──────────────────────────────────
    const handleDownloadTemplate = () => {
        api.downloadImportTemplate();
    };

    const proceedToUpload = () => setStep('upload');

    // ── Step 2: parse CSV and send preview request ───────────────────────────
    const handleFile = useCallback(async (file: File) => {
        setParseError(null);
        if (!file.name.toLowerCase().endsWith('.csv')) {
            setParseError('Please upload a .csv file.');
            return;
        }
        setFileName(file.name);

        Papa.parse<Record<string, string>>(file, {
            header: true,
            skipEmptyLines: true,
            complete: async (results) => {
                if (!results.data || results.data.length === 0) {
                    setParseError('The file contains no data rows.');
                    return;
                }

                const rows = results.data.map(mapCsvRow);
                setParsedRows(rows);

                // Validate required column headers
                const first = results.meta.fields ?? [];
                const firstNorm = first.map(f => f.trim().toLowerCase());
                const required = ['service date', 'provider', 'description', 'amount'];
                const missing = required.filter(r => !firstNorm.includes(r));
                if (missing.length > 0) {
                    setParseError(`Missing required columns: ${missing.join(', ')}. Please use the Steadfast HSA template.`);
                    return;
                }

                // Send preview request
                try {
                    setLoadingPreview(true);
                    const prev = await api.previewBulkImport(rows);
                    if (prev) {
                        setPreview(prev);
                        setStep('preview');
                    }
                } catch (err: unknown) {
                    const msg = err instanceof Error ? err.message : 'Failed to validate the file';
                    setParseError(msg);
                } finally {
                    setLoadingPreview(false);
                }
            },
            error: (err) => {
                setParseError(`CSV parse error: ${err.message}`);
            },
        });
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setDragging(false);
            const file = e.dataTransfer.files?.[0];
            if (file) handleFile(file);
        },
        [handleFile]
    );

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
    };

    // ── Step 3: execute import ───────────────────────────────────────────────
    const handleConfirmImport = async () => {
        if (!preview || preview.errorRows > 0) return;
        try {
            setLoadingImport(true);
            const res = await api.executeBulkImport(parsedRows);
            if (res) {
                setResult(res);
                setStep('success');
                // Fire PostHog event for conversion tracking / email workflows
                Analytics.bulkImportCompleted({
                    countTotal: res.totalImported,
                    countOverLimit: res.countOverLimit,
                    totalValue: res.totalValue,
                    autoCreatedFamilyMembers: res.autoCreatedFamilyMembers,
                    autoCreatedHsaAccounts: res.autoCreatedHsaAccounts,
                });
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Import failed';
            toast.error(msg);
        } finally {
            setLoadingImport(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="p-8 max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/expenses')}
                    className="text-[#718096] hover:text-[#1B365D] transition-colors"
                    aria-label="Back to Expenses"
                >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <div>
                    <h1 className="text-[28px] font-semibold text-[#1B365D]">Import Expenses</h1>
                    <p className="text-[14px] text-[#718096]">Migrate your historical HSA records from a spreadsheet</p>
                </div>
            </div>

            {/* Step indicator */}
            <StepIndicator current={step} />

            {/* ── Step 1: Download template ── */}
            {step === 'download' && (
                <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 space-y-6">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#EBF4FF] flex items-center justify-center flex-shrink-0">
                            <svg className="w-6 h-6 text-[#1B365D]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-[18px] font-semibold text-[#1B365D] mb-1">Step 1: Download the Template</h2>
                            <p className="text-[14px] text-[#4A5568] leading-relaxed">
                                Download our CSV template and fill in your historical expenses. The template includes 8 columns — only
                                <strong> Service Date, Provider, Description,</strong> and <strong>Amount</strong> are required.
                            </p>
                        </div>
                    </div>

                    {/* Column guide */}
                    <div className="rounded-lg bg-[#F7FAFC] border border-[#E2E8F0] overflow-hidden">
                        <table className="w-full text-[13px]">
                            <thead>
                                <tr className="bg-[#EDF2F7]">
                                    <th className="text-left px-4 py-2 font-semibold text-[#4A5568]">Column</th>
                                    <th className="text-left px-4 py-2 font-semibold text-[#4A5568]">Required</th>
                                    <th className="text-left px-4 py-2 font-semibold text-[#4A5568]">Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    { col: 'Service Date', req: true, note: 'MM/DD/YYYY or YYYY-MM-DD, not in the future' },
                                    { col: 'Provider', req: true, note: 'Doctor, pharmacy, clinic name' },
                                    { col: 'Description', req: true, note: 'What was the service or item?' },
                                    { col: 'Amount', req: true, note: 'Decimal number > 0 (e.g., 42.50)' },
                                    { col: 'Category', req: false, note: 'Matched to your expense categories' },
                                    { col: 'Family Member', req: false, note: 'Auto-creates new family members' },
                                    { col: 'HSA Account', req: false, note: 'Auto-creates new HSA accounts' },
                                    { col: 'Notes', req: false, note: 'Any additional context' },
                                ].map(({ col, req, note }) => (
                                    <tr key={col} className="border-t border-[#E2E8F0]">
                                        <td className="px-4 py-2 font-medium text-[#1B365D]">{col}</td>
                                        <td className="px-4 py-2">
                                            {req ? (
                                                <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">Required</span>
                                            ) : (
                                                <span className="text-[11px] text-[#718096]">Optional</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-[#4A5568]">{note}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Free tier callout */}
                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 flex gap-3">
                        <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                            <p className="text-[13px] font-semibold text-amber-800 mb-0.5">Free Tier — Soft Limits</p>
                            <p className="text-[12px] text-amber-700 leading-relaxed">
                                You can import unlimited expenses. Your <strong>first 25</strong> are permanently stored and fully editable.
                                Expenses #26+ are visible and count toward your reimbursable balance, but will be deleted after 30 days
                                unless you upgrade to Core.
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={handleDownloadTemplate}
                            className="flex items-center gap-2 px-5 py-2.5 bg-white border border-[#CBD5E0] text-[#1B365D] rounded-lg text-[14px] font-semibold hover:bg-[#F7FAFC] transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download Template CSV
                        </button>
                        <button
                            onClick={proceedToUpload}
                            className="px-5 py-2.5 bg-[#1B365D] text-white rounded-lg text-[14px] font-semibold hover:opacity-90 transition-opacity"
                        >
                            I've filled it in → Upload Now
                        </button>
                    </div>
                </div>
            )}

            {/* ── Step 2: Upload CSV ── */}
            {step === 'upload' && (
                <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 space-y-5">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#EBF4FF] flex items-center justify-center flex-shrink-0">
                            <svg className="w-6 h-6 text-[#1B365D]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-[18px] font-semibold text-[#1B365D] mb-1">Step 2: Upload Your CSV</h2>
                            <p className="text-[14px] text-[#4A5568]">
                                Drop your populated CSV file below. We'll validate each row and show you a preview before anything is saved.
                            </p>
                        </div>
                    </div>

                    {/* Drop zone */}
                    <div
                        className={`border-2 border-dashed rounded-xl p-12 flex flex-col items-center justify-center cursor-pointer transition-colors ${dragging
                            ? 'border-[#1B365D] bg-[#EBF4FF]'
                            : 'border-[#CBD5E0] bg-[#F7FAFC] hover:border-[#1B365D] hover:bg-[#EBF4FF]'
                            }`}
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={handleDrop}
                    >
                        <svg className="w-10 h-10 text-[#CBD5E0] mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-[14px] font-semibold text-[#4A5568] mb-1">
                            {fileName ? fileName : 'Drop your CSV here, or click to browse'}
                        </p>
                        <p className="text-[12px] text-[#718096]">.csv files only</p>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={handleFileInput}
                    />

                    {parseError && (
                        <div className="rounded-lg bg-red-50 border border-red-200 p-4 flex gap-3">
                            <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-[13px] text-red-700">{parseError}</p>
                        </div>
                    )}

                    {loadingPreview && (
                        <div className="flex items-center gap-3 text-[14px] text-[#718096]">
                            <div className="w-5 h-5 border-2 border-[#1B365D] border-t-transparent rounded-full animate-spin" />
                            Validating {parsedRows.length} rows…
                        </div>
                    )}

                    <div className="flex gap-3">
                        <button
                            onClick={() => setStep('download')}
                            className="px-5 py-2.5 text-[14px] font-semibold text-[#718096] hover:text-[#1B365D] transition-colors"
                        >
                            ← Back
                        </button>
                    </div>
                </div>
            )}

            {/* ── Step 3: Preview ── */}
            {step === 'preview' && preview && (
                <div className="space-y-5">
                    {/* Summary cards */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className="bg-white rounded-xl border border-[#E2E8F0] p-5 text-center">
                            <div className="text-[28px] font-bold text-[#1B365D]">{preview.totalRows}</div>
                            <div className="text-[12px] text-[#718096] mt-1">Total Rows</div>
                        </div>
                        <div className={`rounded-xl border p-5 text-center ${preview.validRows === preview.totalRows ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-[#E2E8F0]'}`}>
                            <div className="text-[28px] font-bold text-emerald-600">{preview.validRows}</div>
                            <div className="text-[12px] text-[#718096] mt-1">Valid Rows</div>
                        </div>
                        <div className={`rounded-xl border p-5 text-center ${preview.errorRows > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-[#E2E8F0]'}`}>
                            <div className={`text-[28px] font-bold ${preview.errorRows > 0 ? 'text-red-600' : 'text-[#718096]'}`}>{preview.errorRows}</div>
                            <div className="text-[12px] text-[#718096] mt-1">Rows with Errors</div>
                        </div>
                    </div>

                    {/* Auto-entity callout */}
                    {(preview.newFamilyMembers.length > 0 || preview.newHsaAccounts.length > 0) && (
                        <div className="bg-[#EBF4FF] border border-[#BEE3F8] rounded-xl p-5">
                            <p className="text-[13px] font-semibold text-[#1B365D] mb-2">
                                🪄 We'll auto-create the following from your data:
                            </p>
                            {preview.newFamilyMembers.length > 0 && (
                                <p className="text-[13px] text-[#2D3748]">
                                    <strong>{preview.newFamilyMembers.length} family member(s):</strong>{' '}
                                    {preview.newFamilyMembers.join(', ')}
                                </p>
                            )}
                            {preview.newHsaAccounts.length > 0 && (
                                <p className="text-[13px] text-[#2D3748] mt-1">
                                    <strong>{preview.newHsaAccounts.length} HSA account(s):</strong>{' '}
                                    {preview.newHsaAccounts.join(', ')}
                                </p>
                            )}
                            {(preview.existingFamilyMembers.length > 0 || preview.existingHsaAccounts.length > 0) && (
                                <p className="text-[12px] text-[#4A5568] mt-2">
                                    Already matched:{' '}
                                    {[...preview.existingFamilyMembers, ...preview.existingHsaAccounts].join(', ')}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Validation error banner */}
                    {preview.errorRows > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex gap-3">
                            <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                                <p className="text-[13px] font-semibold text-red-800">
                                    {preview.errorRows} row(s) have validation errors
                                </p>
                                <p className="text-[12px] text-red-700 mt-1">
                                    Fix the errors in your CSV and re-upload before importing. All rows must be valid to proceed.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Row table */}
                    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E2E8F0]">
                            <h3 className="text-[14px] font-semibold text-[#1B365D]">Row Preview</h3>
                            <label className="flex items-center gap-2 text-[13px] text-[#718096] cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={showErrorsOnly}
                                    onChange={e => setShowErrorsOnly(e.target.checked)}
                                    className="rounded"
                                />
                                Show errors only
                            </label>
                        </div>
                        <div className="p-4 max-h-[400px] overflow-y-auto">
                            <PreviewTable rows={preview.rows} showErrorsOnly={showErrorsOnly} />
                        </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center justify-between">
                        <div className="flex gap-3">
                            <button
                                onClick={() => { setStep('upload'); setPreview(null); setFileName(null); setParseError(null); }}
                                className="px-5 py-2.5 text-[14px] font-semibold text-[#718096] hover:text-[#1B365D] transition-colors"
                            >
                                ← Re-upload
                            </button>
                        </div>
                        <button
                            onClick={handleConfirmImport}
                            disabled={preview.errorRows > 0 || preview.validRows === 0 || loadingImport}
                            className="px-6 py-2.5 bg-[#1B365D] text-white rounded-lg text-[14px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {loadingImport && (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            )}
                            Import {preview.validRows} Expense{preview.validRows !== 1 ? 's' : ''}
                        </button>
                    </div>
                </div>
            )}

            {/* ── Step 4: Success ── */}
            {step === 'success' && result && (
                <div className="bg-white rounded-xl border border-[#E2E8F0] p-10 text-center space-y-6">
                    <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto">
                        <svg className="w-8 h-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>

                    <div>
                        <h2 className="text-[24px] font-bold text-[#1B365D] mb-2">Import Complete!</h2>
                        <p className="text-[14px] text-[#718096]">
                            Your historical expenses are now in Steadfast HSA.
                        </p>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto">
                        <div className="rounded-xl bg-[#F7FAFC] p-4">
                            <div className="text-[24px] font-bold text-[#1B365D]">{result.totalImported}</div>
                            <div className="text-[12px] text-[#718096]">Expenses Imported</div>
                        </div>
                        <div className="rounded-xl bg-[#F7FAFC] p-4">
                            <div className="text-[24px] font-bold text-emerald-600">
                                ${result.totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                            <div className="text-[12px] text-[#718096]">Total Reimbursable</div>
                        </div>
                    </div>

                    {/* Auto-created entities */}
                    {(result.autoCreatedFamilyMembers > 0 || result.autoCreatedHsaAccounts > 0) && (
                        <div className="text-[13px] text-[#4A5568] space-y-1">
                            {result.autoCreatedFamilyMembers > 0 && (
                                <p>✓ Created {result.autoCreatedFamilyMembers} family member(s): {result.createdFamilyMemberNames.join(', ')}</p>
                            )}
                            {result.autoCreatedHsaAccounts > 0 && (
                                <p>✓ Created {result.autoCreatedHsaAccounts} HSA account(s): {result.createdHsaAccountNames.join(', ')}</p>
                            )}
                        </div>
                    )}

                    {/* Soft limit warning */}
                    {result.countOverLimit > 0 && (
                        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-left flex gap-3">
                            <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <div>
                                <p className="text-[13px] font-semibold text-amber-800">
                                    {result.countOverLimit} expenses exceed your Free tier limit
                                </p>
                                <p className="text-[12px] text-amber-700 mt-1">
                                    They're visible and count toward your balance today, but will be deleted in 30 days.
                                    Upgrade to Core to keep them permanently.
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="flex gap-3 justify-center">
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="px-6 py-2.5 bg-[#1B365D] text-white rounded-lg text-[14px] font-semibold hover:opacity-90 transition-opacity"
                        >
                            View Dashboard
                        </button>
                        <button
                            onClick={() => navigate('/expenses')}
                            className="px-5 py-2.5 border border-[#CBD5E0] text-[#1B365D] rounded-lg text-[14px] font-semibold hover:bg-[#F7FAFC] transition-colors"
                        >
                            View Expenses
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
